import {
  Navigate, Route, Routes, useLocation,
} from 'react-router-dom';
import PhysicalTicketsTemplate from './pages/PhysicalTicketsTemplate';
import PageTransition from './components/PageTransition';
import getSubdomain from './utils/getSubdomain';
import BusinessLanding from './pages/BusinessLanding';
import AvailableEventsPage from './pages/AvailableEventsPage';
import CreateOrderOutlet from './components/CreateOrderOutlet';
import SelectTicketsPage from './pages/SelectTicketsPage';
import SelectSeatsPage from './pages/SelectSeatsPage';
import CompleteEntryFieldsPage from './pages/CompleteEntryFields';
import QuestionsPage from './pages/QuestionsPage';
import CheckoutPage from './pages/CheckoutPage';
import TimeoutPage from './pages/TimeoutPage';
import SignUpPage from './pages/SignUpPage';
import MemberRequestPage from './pages/MemberRequestPage';
import SignUpConfirmPage from './pages/SignUpConfirmPage';
import OrderReferralPage from './pages/OrderReferralPage';
import QrDisplay from './pages/qrDisplay';
import EventPage from './pages/EventPage';
import SimplifiedSalePage from './pages/SimplifiedSalePage';
import CreateOrderSuccess from './pages/CreateOrderSuccess';
import CreateOrderError from './pages/CreateOrderError';
import LogIn from './pages/LogIn';
import MPOAuthRedirectPage from './pages/MPOAuthRedirectPage';
import EventListsPage from './pages/EventListsPage';
import EventListMenu from './pages/EventListMenu';
import EventListGuests from './pages/EventListGuests';
import EventListValidator from './pages/EventListValidator';
import AdminOutlet from './pages/AdminOutlet';
import Dashboard from './pages/Dashboard';
import OnBoardingOutletPage from './pages/OnboardingOutlet';
import BusinessEditor from './components/BusinessEditor';
import EventEditor from './components/EventEditor';
import CreateTicket from './components/CreateTicket';
import MapCreator from './components/MapCreator';
import AvanttiReportPage from './pages/AvanttiReportPage';
import SuperAdmin from './pages/SuperAdmin';
import BusinessMenu from './pages/BusinessMenu';
import AdminEvents from './pages/AdminEvents';
import InvitationsPage from './pages/InvitationsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminCustomersMenu from './pages/AdminCustomersMenu';
import AdminCustomersPage from './pages/AdminCustomersPage';
import AdminCustomersGroups from './pages/AdminCustomersGroups';
import MercadoPagoIntegrationPage from './pages/MercadoPagoIntegrationPage';
import CashRegisterPage from './pages/CashRegisterPage';
import MembersPage from './pages/MembersPage';
import EventMenu from './pages/EventMenu';
import DiscountPage from './pages/AudienceDiscountPage';
import QRCodeScanner from './components/QRCodeScanner';
import InPersonSalePage from './pages/InPersonSalePage';
import EventOrders from './pages/EventOrders';
import InPersonPage from './pages/InPersonPage';
import SellersSummaryDetails from './components/SellersSummaryDetails';
import AdminTickets from './pages/AdminTickets';
import AdminShowtimes from './components/AdminShowtimes';
import EventSalesReportPage from './pages/EventSalesReportPage';
import EventSalesReportPdfPage from './pages/EventSalesReportPdfPage';
import BalancePage from './pages/BalancePage';
import CRUDCoupon from './components/CRUDCoupon';
import AdminReferralsPage from './pages/AdminReferralsPage';
import AdminOrderReferralsPage from './pages/AdminOrderReferralsPage';
import PaymentsPage from './pages/PaymentsPage';
import AdminSellersPage from './pages/AdminSellersPage';
import PollsPage from './pages/PollsPage';
import AdminScanningPage from './pages/AdminScanningPage';
import AdminScannerTypesPage from './pages/AdminScannerTypesPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const orderCreationRoutes = (page: (component: React.ReactNode) => React.ReactNode) => (
  <>
    <Route path="tickets" element={page(<SelectTicketsPage />)} />
    <Route path="seats" element={page(<SelectSeatsPage />)} />
    <Route path="entry-data" element={page(<CompleteEntryFieldsPage />)} />
    <Route path="poll" element={page(<QuestionsPage />)} />
    <Route path="checkout" element={page(<CheckoutPage />)} />
    <Route path="timeout" element={page(<TimeoutPage />)} />
  </>
);

const adminOrderCreationRoutes = (page: (component: React.ReactNode) => React.ReactNode) => (
  <>
    <Route path="manual" element={page(<SelectTicketsPage />)} />
    {orderCreationRoutes(page)}
  </>
);

const PublicRoutes = ({ page, subdomain }: { page: (component: React.ReactNode) => React.ReactNode, subdomain: string | null }) => (
  <>
    <Route path="/" element={page(subdomain ? <BusinessLanding subdomain={subdomain} /> : <AvailableEventsPage />)} />
    {subdomain && (
      <Route path="orders/new" element={<CreateOrderOutlet />}>
        {orderCreationRoutes(page)}
      </Route>
    )}
    <Route path="/signup" element={page(<SignUpPage />)} />
    <Route path="/reset-password" element={page(<ResetPasswordPage />)} />
    <Route path="/empezar" element={page(<SignUpPage />)} />
    <Route path="/socios" element={page(<MemberRequestPage />)} />
    <Route path="/signup-confirm" element={page(<SignUpConfirmPage />)} />
    <Route path="/order-referrals/:order_id" element={page(<OrderReferralPage />)} />
    <Route path=":event_slug/:order_id" element={page(<QrDisplay />)} />
    <Route path="/events/:event_slug/orders/:order_id" element={page(<QrDisplay />)} />
    <Route path="/events/:event_id">
      <Route index element={page(<EventPage />)} />
      <Route path="simplified-sale" element={page(<SimplifiedSalePage />)} />
      <Route path="orders/new" element={<CreateOrderOutlet />}>
        {orderCreationRoutes(page)}
      </Route>
    </Route>
    <Route path="/order-success" element={page(<CreateOrderSuccess />)} />
    <Route path="/order-error" element={page(<CreateOrderError />)} />
    <Route path="/login" element={page(<LogIn />)} />
    <Route path="/mercadopago-oauth" element={page(<MPOAuthRedirectPage />)} />
    <Route path="/lists" element={page(<EventListsPage />)} />
    <Route path="/lists/:list_id" element={page(<EventListMenu />)} />
    <Route path="/lists/:list_id/guests" element={page(<EventListGuests />)} />
    <Route path="/lists/:list_id/validator" element={page(<EventListValidator />)} />
  </>
);

const AdminRoutes = ({ page }: { page: (component: React.ReactNode) => React.ReactNode }) => (
  <Route path="/admin" element={<AdminOutlet />}>
    <Route index element={page(<Dashboard />)} />
    <Route path="onboarding" element={<OnBoardingOutletPage />}>
      <Route index element={page(<Navigate to="create-business" replace />)} />
      <Route path="create-business" element={page(<BusinessEditor onBoarding />)} />
      <Route path=":business_id/create-event" element={page(<EventEditor create />)} />
      <Route path=":business_id/:event_id/create-ticket" element={page(<CreateTicket showtimeId={null} onSuccess={() => {}} onCancel={() => {}} />)} />
    </Route>
    <Route path="map-creator" element={page(<MapCreator />)} />
    <Route path="avantti-report" element={page(<AvanttiReportPage />)} />
    <Route path="superadmin" element={page(<SuperAdmin />)} />
    <Route path="dashboard" element={page(<Dashboard />)} />
    <Route path="businesses" element={page(<BusinessMenu />)} />
    <Route path="all-events" element={page(<AdminEvents allUserEvents />)} />
    <Route path="invitations" element={page(<InvitationsPage />)} />
    <Route path="create-business" element={page(<BusinessEditor create />)} />
    <Route path=":business_id/edit-business" element={page(<BusinessEditor create={false} />)} />
    <Route path=":business_id/users" element={page(<AdminUsersPage />)} />
    <Route path=":business_id/customers" element={page(<AdminCustomersMenu />)} />
    <Route path=":business_id/customers/clients" element={page(<AdminCustomersPage />)} />
    <Route path=":business_id/customers/groups" element={page(<AdminCustomersGroups />)} />
    <Route path=":business_id/mercadopago" element={page(<MercadoPagoIntegrationPage />)} />
    <Route path=":business_id/cash-register" element={page(<CashRegisterPage />)} />
    <Route path=":business_id/members" element={page(<MembersPage />)} />
    <Route path="order-success" element={page(<CreateOrderSuccess />)} />
    <Route path="order-error" element={page(<CreateOrderError />)} />
    <Route path=":business_id/events">
      <Route index element={page(<AdminEvents />)} />
      <Route path="create" element={page(<EventEditor create />)} />
      <Route path=":event_id">
        <Route index element={page(<EventMenu />)} />
        <Route path="list/:list_id" element={page(<EventListMenu />)} />
        <Route path="list/:list_id/guests" element={page(<EventListGuests />)} />
        <Route path="list/:list_id/validator" element={page(<EventListValidator />)} />
        <Route path="discounts" element={page(<DiscountPage />)} />
        <Route path="scanner" element={page(<QRCodeScanner />)} />
        <Route path="inperson/:cashier_id/:session_id" element={page(<InPersonSalePage />)} />
        <Route path="inperson/:cashier_id" element={page(<InPersonSalePage view />)} />
        <Route path="scanning" element={page(<AdminScanningPage />)} />
        <Route path="orders">
          <Route index element={page(<EventOrders />)} />
          <Route path="simplified-manual" element={page(<SimplifiedSalePage />)} />
          <Route path="new" element={page(<CreateOrderOutlet />)}>
            {adminOrderCreationRoutes(page)}
          </Route>
          <Route path="physical-tickets" element={page(<PhysicalTicketsTemplate />)} />

        </Route>
        <Route path="inperson" element={page(<InPersonPage />)} />
        <Route path="details" element={page(<SellersSummaryDetails />)} />
        <Route path="tickets" element={page(<AdminTickets />)} />
        <Route path="showtimes" element={page(<AdminShowtimes />)} />
        <Route path="sales-report" element={page(<EventSalesReportPage />)} />
        <Route path="sales-report-pdf" element={page(<EventSalesReportPdfPage />)} />
        <Route path="balance" element={page(<BalancePage />)} />
        <Route path="users" element={page(<AdminUsersPage />)} />
        <Route path="coupons" element={page(<CRUDCoupon />)} />
        <Route path="referrals" element={page(<AdminReferralsPage />)} />
        <Route path="order-referrals" element={page(<AdminOrderReferralsPage />)} />
        <Route path="payments" element={page(<PaymentsPage />)} />
        <Route path="sellers">
          <Route index element={page(<AdminSellersPage />)} />
          <Route path=":seller_id/details" element={page(<SellersSummaryDetails />)} />
        </Route>
        <Route path="polls" element={page(<PollsPage />)} />
        <Route path="scanner-types" element={page(<AdminScannerTypesPage />)} />
        <Route path="edit" element={page(<EventEditor create={false} />)} />
      </Route>
    </Route>
  </Route>
);

export default function AppRoutes() {
  const location = useLocation();
  const subdomain = getSubdomain();
  const page = (component: React.ReactNode) => <PageTransition>{component}</PageTransition>;
  return (
    <Routes location={location} key={location.pathname}>
      {PublicRoutes({ page, subdomain })}
      {AdminRoutes({ page })}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
